/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { faArrowDown, faArrowUp, faPlus, faTrash, faUpload } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import { ReactNode, useEffect, useMemo, type FC } from 'react'
import { Alert, Button, Col, Form, InputGroup, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import Select from 'react-select'
import * as yup from 'yup'
import { Product } from '../../gql/graphql'
import FileUploader from '../FileUploader'

const validationSchema = yup.object().shape({
  __typename: yup.string().oneOf(['Recipe', 'Tip']).default('Recipe'),
  id: yup.lazy(value => typeof value === 'string'
    ? yup.string().nullable()
    : yup.number().nullable()
  ),
  name: yup.string().min(1, 'O nome é obrigatório').max(255, 'O nome deve conter no máximo 255 caracteres').required('O nome é obrigatório').default(''),
  slug: yup.string().required('O slug é obrigatório').default('').min(0).max(255).matches(/^[a-z0-9-]+$/, 'O slug deve conter apenas letras minúsculas, números e hífens'),
  description: yup.string().nullable().default(''),
  video: yup.string().nullable(),
  image: yup.lazy(value => typeof value === 'object'
    ? yup.object().shape({ url: yup.string(), mimetype: yup.string() }).nullable()
    : yup.string().nullable()
  ),
  category: yup.lazy(value => typeof value === 'string'
    ? yup.string().nullable()
    : yup.number().nullable()
  ),
  status: yup.string().oneOf(['active', 'inactive', 'draft', 'deleted']).default('draft'),
  difficulty: yup.string().oneOf(['easy', 'medium', 'hard']).default('medium'),
  preparationTime: yup.number().default(0),
  servings: yup.number().default(1),
  ingredients: yup.array().of(yup.object().shape({
    type: yup.string().oneOf(['title', 'item']),
    value: yup.string()
  })).default([]),
  instructions: yup.array().of(yup.object().shape({
    type: yup.string().oneOf(['title', 'item']),
    value: yup.string()
  })).default([]),
  products: yup.array().of(yup.object().shape({
    sku: yup.string(),
    description: yup.string()
  })).default([]),
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormRecipeHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  categories: {
    id: string | number,
    name: string
  }[],
  products: Product[]
  onSubmit?: FormRecipeHandleSubmit,
  initialValues?: Partial<ValidationSchema>,
  isLoading?: boolean,
  customFooterMessage?: ReactNode,
  onChange?: (values: ValidationSchema) => void
}

/**
 * FormRecipe is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormRecipe from './src/views/PostEdit/FormRecipe.tsx'
 *
 * const MyComponent = () => (
 *   <FormRecipe />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormRecipe: FC<Props> = ({
  onSubmit = () => {},
  initialValues = {},
  isLoading = false,
  customFooterMessage = null,
  onChange = () => {},
  categories = [],
  products = []
}) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  useEffect(() => {
    onChange(formik.values)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values, formik.initialValues])

  return (
    <Form className='mb-3 mt-3' onSubmit={formik.handleSubmit}>
      <Row>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Nome</Form.Label>
          <Form.Control
            type='text'
            placeholder='Informe o título da postagem'
            name='name'
            value={formik?.values?.name ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Slug</Form.Label>
          <InputGroup className="mb-3">
            <InputGroup.Text id="basic-addon1">
              https://www.supermercados-mundial.com.br/receitas/
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="minha-pagina"
              name='slug'
              value={formik.values.slug}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </InputGroup>
          <Form.Text className="text-muted">
            O slug é utilizado para identificar a página na URL do site,
            {' '}
            por exemplo: https://www.supermercados-mundial.com.br/pagina-personalizada
          </Form.Text>
        </Form.Group>
        <Form.Group as={Col} md={2} className="mb-3">
          <Form.Label>Situação</Form.Label>
          <Form.Select
            name='status'
            value={formik?.values?.status}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          >
            <option selected>Selecione</option>
            <option value='draft'>📝 Rascunho</option>
            <option value='active'>🌎 Pública</option>
            <option value='inactive'>🔒 Privada</option>
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md={2} className="mb-3">
          <Form.Label>Tipo</Form.Label>
          <Form.Select
            name='__typename'
            value={formik?.values?.__typename}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          >
            <option>Selecione</option>
            <option value='Recipe'>Receita</option>
            <option value='Tip'>Dica</option>
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md={2} className="mb-3">
          <Form.Label>Categoria</Form.Label>
          <Form.Select
            name='category'
            value={formik?.values?.category as any}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading || formik.values.__typename === 'Tip'}
          >
            <option>Selecione uma opção</option>
            {categories?.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Col md={2} className="mb-3">
          <Form.Label>Preparo</Form.Label>
          <InputGroup>
            <Form.Control
              type='number'
              name='preparationTime'
              value={formik?.values?.preparationTime ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
            <InputGroup.Text id="basic-addon2">minutos</InputGroup.Text>
          </InputGroup>
        </Col>
        <Form.Group as={Col} md={2} className="mb-3">
          <Form.Label>Porções</Form.Label>
          <Form.Control
            type='number'
            name='servings'
            value={formik?.values?.servings ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading || formik.values.__typename === 'Tip'}
          />
        </Form.Group>
        <Form.Group as={Col} md={2} className="mb-3">
          <Form.Label>Dificuldade</Form.Label>
          <Form.Select
            name='status'
            value={formik?.values?.difficulty}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          >
            <option>Selecione</option>
            <option value='easy'>Fácil</option>
            <option value='medium'>Médio</option>
            <option value='hard'>Difícil</option>
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>
            Resumo
            {' '}
            <small>({formik?.values?.description?.length} de 255 caracteres)</small>
          </Form.Label>
          <Form.Control
            as='textarea'
            rows={4}
            placeholder='Informe o resumo sobre a receita ou dica'
            name='description'
            maxLength={255}
            value={formik?.values?.description ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <Form.Label>Arquivo de imagem (obrigatório 820x512px)</Form.Label>
          <br />
          <FileUploader
            variant='outline-primary'
            accept='image/*'
            size='sm'
            resolutionWidth={820}
            resolutionHeight={512}
            resolutionType='absolute'
            disabled={formik.isSubmitting || isLoading}
            onReceiveFiles={([{ dataURL }]) =>
              formik.setFieldValue('image', dataURL)
            }
            onError={error => console.error(error.message)}
          >
            <FontAwesomeIcon icon={faUpload} />
            {' '}
            Enviar arquivo
          </FileUploader>
        </Form.Group>
        <Col md={12}>
          <Row>
            <Col md={12}>
              <Row style={{ marginBottom: 10 }}>
                <Col md={11}>
                  Ingredientes
                </Col>
                <Col md={1}>
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      formik.setFieldValue('ingredients', [
                        ...formik.values.ingredients,
                        { type: 'item', value: '' }
                      ])
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </Button>
                </Col>
              </Row>
            </Col>
            {formik?.values?.ingredients.length === 0 && (
              <small>
                <p style={{ textAlign: 'center' }}>Nenhum ingrediente informado</p>
              </small>
            )}
            {formik?.values?.ingredients?.map((ingredient, index) => (
              <Col key={index} md={12} className="mb-1">
                <InputGroup>
                  <Form.Select
                    style={{ maxWidth: 140 }}
                    value={ingredient.type}
                    name={`ingredients[${index}].type`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={formik.isSubmitting || isLoading}
                  >
                    <option>Selecione</option>
                    <option value='title'>Titulo</option>
                    <option value='item'>Ingrediente</option>
                  </Form.Select>
                  <Form.Control
                    type='text'
                    placeholder='Informe o ingrediente'
                    name={`ingredients[${index}].value`}
                    value={ingredient.value}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={formik.isSubmitting || isLoading}
                  />
                  <Button
                    variant="outline-primary"
                    disabled={index === formik.values.ingredients.length - 1}
                    onClick={() => {
                      const ingredients = formik.values.ingredients
                      const item = ingredients[index]
                      ingredients[index] = ingredients[index + 1]
                      ingredients[index + 1] = item
                      formik.setFieldValue('ingredients', ingredients)
                    }}
                  >
                    <FontAwesomeIcon icon={faArrowDown} />
                  </Button>
                  <Button
                    variant="outline-primary"
                    disabled={index === 0}
                    onClick={() => {
                      const ingredients = formik.values.ingredients
                      const item = ingredients[index]
                      ingredients[index] = ingredients[index - 1]
                      ingredients[index - 1] = item
                      formik.setFieldValue('ingredients', ingredients)
                    }}
                  >
                    <FontAwesomeIcon icon={faArrowUp} />
                  </Button>
                  <Button
                    variant="outline-danger"
                    onClick={() => {
                      formik.setFieldValue('ingredients', formik.values.ingredients.filter((_, i) => i !== index))
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </InputGroup>
              </Col>
            ))}
          </Row>
        </Col>
        <Col md={12} style={{ marginTop: 10 }}>
          <Row>
            <Col md={12}>
              <Row style={{ marginBottom: 10 }}>
                <Col md={11}>
                  Instruções
                </Col>
                <Col md={1}>
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      formik.setFieldValue('instructions', [
                        ...formik.values.instructions,
                        { type: 'item', value: '' }
                      ])
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </Button>
                </Col>
              </Row>
            </Col>
            {formik?.values?.instructions.length === 0 && (
              <small>
                <p style={{ textAlign: 'center' }}>Nenhum instrução informada</p>
              </small>
            )}
            {formik?.values?.instructions?.map((instruction, index) => (
              <Col key={index} md={12} className="mb-1">
                <InputGroup>
                  <Form.Select
                    style={{ maxWidth: 140 }}
                    value={instruction.type}
                    name={`instruction[${index}].type`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={formik.isSubmitting || isLoading}
                  >
                    <option>Selecione</option>
                    <option value='title'>Titulo</option>
                    <option value='item'>Instrução</option>
                  </Form.Select>
                  <Form.Control
                    type='text'
                    placeholder='Descreva a instrução'
                    name={`instructions[${index}].value`}
                    value={instruction.value}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={formik.isSubmitting || isLoading}
                  />
                  <Button
                    variant="outline-primary"
                    disabled={index === formik.values.instructions.length - 1}
                    onClick={() => {
                      const instructions = formik.values.instructions
                      const item = instructions[index]
                      instructions[index] = instructions[index + 1]
                      instructions[index + 1] = item
                      formik.setFieldValue('instructions', instructions)
                    }}
                  >
                    <FontAwesomeIcon icon={faArrowDown} />
                  </Button>
                  <Button
                    variant="outline-primary"
                    disabled={index === 0}
                    onClick={() => {
                      const instructions = formik.values.instructions
                      const item = instructions[index]
                      instructions[index] = instructions[index - 1]
                      instructions[index - 1] = item
                      formik.setFieldValue('instructions', instructions)
                    }}
                  >
                    <FontAwesomeIcon icon={faArrowUp} />
                  </Button>
                  <Button
                    variant="outline-danger"
                    onClick={() => {
                      formik.setFieldValue(
                        'instructions',
                        formik.values.instructions.filter((_, i) => i !== index)
                      )
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </InputGroup>
              </Col>
            ))}
          </Row>
        </Col>
        <Form.Group as={Col} md={12} style={{ marginTop: 20, marginBottom: 20 }}>
          <Form.Label>Produtos recomendados</Form.Label>
          <Select
            isMulti
            defaultValue={formik?.values?.products.map(product => ({
              value: product.sku,
              label: `${product.sku} - ${product.description}`
            }))}
            options={products?.map(product => ({
              value: product.sku,
              label: `${product.sku} - ${product.description}`
            }))}
            onChange={selected => {
              formik.setFieldValue(
                'products',
                selected.map(({ value }) => products.find(product => product.sku === value))
              )
            }}
          />
        </Form.Group>
        <Col>
          <Button
            variant="link"
            style={{ marginRight: 10 }} onClick={() => navigate('/recipes')}
            disabled={formik.isSubmitting || isLoading}
          >
            Voltar
          </Button>
          <Button
            type="submit"
            disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
          >
            Salvar
          </Button>
          {' '}
          {customFooterMessage}
        </Col>
        <Col md={12} style={{ paddingTop: 10 }}>
          {formik?.errors && Object.keys(formik.errors).length > 0 && (
            <>
              {Object.values(formik.errors).map((error, index) => (
                <Alert variant='warning' key={index}>
                  {error.toString()}
                </Alert>
              ))}
            </>
          )}
        </Col>
      </Row>
    </Form>
  )
}

export default FormRecipe
